<script lang="ts">
    import {
        getModalStore,
        type ModalStore,
        Autocomplete,
        type AutocompleteOption,
        popup,
        getToastStore,
        type ToastStore,
    } from "@skeletonlabs/skeleton";

    import BlackX from "$lib/assets/BlackX.svg";
    import CheckIcon from "$lib/assets/CheckIcon.svg";
    import MinusIcon from "$lib/assets/MinusIcon.svg";

    const modalStore: ModalStore = getModalStore();
    const toastStore: ToastStore = getToastStore();

    // Parameters
    const type: "question" | "document" = $modalStore[0].meta?.type;
    let object_list: [] = [];
    let application_id: string | undefined = undefined;
    const tags: {} = $modalStore[0].meta?.tags;
    const categories: {} = $modalStore[0].meta?.categories;
    if (type == "question") {
        object_list = $modalStore[0].meta?.question_list;
        application_id = $modalStore[0].meta?.application_id;
    } else if (type == "document") {
        object_list = $modalStore[0].meta?.document_list;
    }

    const endpoint = type == "question" ? "/api/drafts/question_tags" : "/api/documents/tags";

    let updating_tags: boolean = false;

    let tags_count = {};

    for (let obj of object_list) {
        for (let tag of obj.tags) {
            if (!tags_count[tag]) {
                tags_count[tag] = 1;
            } else {
                tags_count[tag] += 1;
            }
        }
    }
    // Set up autocomplete input for adding tags
    let tags_input: string = "";
    let tags_options: AutocompleteOption<string>[];
    $: tags_options = Object.entries(tags)
        .map(([tag_id, tag]) => ({ value: tag_id, label: tag.name, keywords: tag.name, category: tag.category }))
        .sort((a, b) => a.category.localeCompare(b.category))
        .filter((ele) => !object_list.some((obj) => obj.tags.includes(ele.value)));

    function request_body(ids: string[], tag_id: string, application_id?: string) {
        const body = {
            ids: ids,
            tag_id: tag_id,
        };
        if (application_id) {
            body.application_id = application_id;
        }
        return JSON.stringify(body);
    }

    type tags_modal_response = {
        type: "add" | "delete";
        tag_id: string;
    };

    async function handle_selection(event: CustomEvent<AutocompleteOption<string>>): Promise<void> {
        if (updating_tags) return;
        updating_tags = true;

        const tag_id = event.detail.value;

        const resp = await fetch(endpoint, {
            method: "POST",
            body: request_body(object_list.map((ele) => ele.id), tag_id, application_id),
        });

        if (resp.ok) {
            const res = await resp.json();

            toastStore.trigger({
                message: "✓ " + res.message,
                background: "bg-zinc-800 text-green-300 text-center",
                hideDismiss: true,
                timeout: 4000,
            });

            $modalStore[0].response!({
                type: "add",
                tag_id: event.detail.value,
            } as tags_modal_response);

            tags_count[tag_id] = object_list.length;
            tags_count = tags_count;

            object_list.forEach((obj) => {
                if (!obj.tags.includes(tag_id)) obj.tags.push(tag_id);
                obj.tags = obj.tags.sort(
                    (a, b) => tags[a].category.localeCompare(tags[b].category) || tags[a].name.localeCompare(tags[b].name),
                );
            });

            object_list = object_list;
        } else {
            await resp
                .json()
                .then((res) => {
                    toastStore.trigger({
                        message: "✗ " + res.message,
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                })
                .catch(() => {
                    toastStore.trigger({
                        message: "✗ Something went wrong. Please try again or contact us if this error persists.",
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                });
        }

        updating_tags = false;
        tags_input = "";
    }

    async function handle_deletion(tag_id: string): Promise<void> {
        if (updating_tags) return;
        updating_tags = true;

        const resp = await fetch(endpoint, {
            method: "DELETE",
            body: request_body(object_list.map((obj) => obj.id), tag_id, application_id),
        });

        if (resp.ok) {
            const res = await resp.json();

            toastStore.trigger({
                message: "✓ " + res.message,
                background: "bg-zinc-800 text-green-300 text-center",
                hideDismiss: true,
                timeout: 4000,
            });

            $modalStore[0].response!({
                type: "delete",
                tag_id: tag_id,
            } as tags_modal_response);

            delete tags_count[tag_id];
            tags_count = tags_count;

            object_list = object_list;
        } else {
            await resp
                .json()
                .then((res) => {
                    toastStore.trigger({
                        message: "✗ " + res.message,
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                })
                .catch(() => {
                    toastStore.trigger({
                        message: "✗ Something went wrong. Please try again or contact us if this error persists.",
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 8000,
                    });
                });
        }
        updating_tags = false;
    }
</script>

<div class="bg-white p-8 rounded-lg">
    <div class="flex items-center">
        <p class="font-bold">Edit Tags</p>
        <button
            class="ml-auto rounded-full hover:bg-stone-100 transition duration-300 p-1"
            on:click={() => modalStore.clear()}
        >
            <img src={BlackX} alt="Exit" />
        </button>
    </div>

    <p class="text-sm mt-4 mb-2">Add Tag</p>
    <input
        class="rounded-full text-sm w-80 border-zinc-400"
        type="text"
        placeholder="Search for a tag"
        autocomplete="off"
        bind:value={tags_input}
        use:popup={{
            event: "focus-click",
            target: "popup-autocomplete",
            placement: "bottom",
        }}
    />
    <div data-popup={"popup-autocomplete"} class="bg-zinc-200 text-xs rounded-lg shadow-md py-3 w-80 z-[500]">
        <Autocomplete bind:input={tags_input} options={tags_options} on:selection={handle_selection} />
    </div>

    <div class="mt-4 flex flex-col">
        {#each Object.keys(categories).toSorted() as category}
            <div class="text-sm font-semibold my-2">{category}</div>
            {#each categories[category] as tag}
                <div class="flex items-center space-x-2 py-1">
                    <button
                        class="w-5 h-5 flex items-center justify-center rounded border border-gray-300"
                        on:click={() =>
                            object_list.some((obj) => obj.tags.includes(tag.tag_id))
                                ? handle_deletion(tag.tag_id)
                                : handle_selection({ detail: { value: tag.tag_id } })}
                    >
                        {#if object_list.every((obj) => obj.tags.includes(tag.tag_id))}
                            <img src={CheckIcon} alt="All Selected" class="w-3 h-3" />
                        {:else if object_list.some((obj) => obj.tags.includes(tag.tag_id))}
                            <img src={MinusIcon} alt="Some Selected" class="w-3 h-3" />
                        {:else}
                            <div />
                        {/if}
                    </button>
                    <span class="text-sm">{tag.name}</span>
                    <span class="text-xs text-zinc-500">({tags_count[tag.tag_id] ?? 0} of {object_list.length})</span>
                </div>
            {/each}
        {/each}
    </div>
</div>
